import { useRef, useState } from "react"
import { Alert, Box, Button, CircularProgress, Select, Stack, Typography } from "@mui/material"
import { Repeat } from "lib/@components/repeat"
import { If } from "lib/@components/switch"
import { isEnabled } from "lib/@components/feature"
import { FEATURE_FLAG_OPTIMISER_CONDITIONAL_TRADES } from "../../feature-flag-conditional-trades"
import { FEATURE_FLAG_SCHEDULE_ASSIGNMENTS } from "../../feature-flag-schedule-assignments"
import { getScenarioCostTasks } from "routes/scenarios/controller/scenario-cost-tasks"
import { VariantBreakdownTaskRow } from "./variant-breakdown-task-row"
import {
    buildResolutionMenuItems,
    RESOLUTION_FILTER_ANY,
    RESOLUTION_FILTER_ASSIGNMENT_ONLY,
    RESOLUTION_FILTER_CONDITIONAL_ONLY,
    RESOLUTION_FILTER_DEFAULT_ONLY,
} from "./resolution-filter-options"
const TASK_PAGE_SIZE = 100

const EMPTY_PAGES = { items: [], endCursor: null, hasNextPage: false, totalCount: null, conditionOptions: null }

// Database-backed task detail: nothing loads until "Show Tasks", "Show more"
// appends the next keyset page, and filter changes discard accumulated pages and
// start a new server query (resolution/condition filters run on the indexed rows).
export function VariantBreakdownServerTasks({ variant, scenarioId, currency }) {
    const costResult = variant?.costResult
    // The stored detail-row count, NOT costResult.taskCount: that counts every task
    // the engine saw, including fixed-annual, unmapped, unpriced and skill-less tasks
    // which never produce a detail row. Using it would claim more tasks than the list
    // can ever show, then appear to shrink once a page loaded.
    const totalTaskCount = costResult?.detailsRef?.taskCount ?? 0
    const [showTasks, setShowTasks] = useState(false)
    const [resolutionFilter, setResolutionFilter] = useState(RESOLUTION_FILTER_ANY)
    const [pages, setPages] = useState(EMPTY_PAGES)
    const [loading, setLoading] = useState(false)
    const latestRequest = useRef(0)
    const [error, setError] = useState(null)
    const conditionalTradesOn = isEnabled(FEATURE_FLAG_OPTIMISER_CONDITIONAL_TRADES)
    const scheduleAssignmentsOn = isEnabled(FEATURE_FLAG_SCHEDULE_ASSIGNMENTS)
    const conditionalNames = getConditionalNames()
    const filteredCount = pages.totalCount ?? totalTaskCount

    return (
        <If value={totalTaskCount > 0}>
            <Box>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" data-cy="variant-breakdown-task-summary">
                        Task Detail ({filteredCount} of {totalTaskCount} tasks)
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <If value={conditionalTradesOn || scheduleAssignmentsOn}>
                            <Select
                                size="small"
                                value={resolutionFilter}
                                onChange={handleResolutionFilterChange}
                                sx={{ minWidth: 200, fontSize: 13 }}
                                data-cy="resolution-filter"
                            >
                                {buildResolutionMenuItems({
                                    conditionalTradesOn,
                                    scheduleAssignmentsOn,
                                    conditionalNames,
                                })}
                            </Select>
                        </If>
                        <Button size="small" onClick={handleToggleTasks} data-cy="variant-breakdown-toggle-tasks">
                            <If value={showTasks}>Hide Tasks</If>
                            <If value={!showTasks}>Show Tasks</If>
                        </Button>
                    </Stack>
                </Stack>
                <If value={!!error}>
                    <Alert severity="error" sx={{ mb: 1 }}>
                        {error}
                    </Alert>
                </If>
                <If value={showTasks}>
                    <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, overflow: "hidden" }}>
                        <Stack
                            direction="row"
                            sx={{ px: 2, py: 0.5, borderBottom: 1, borderColor: "divider", bgcolor: "action.hover" }}
                        >
                            <Box sx={{ flex: 3 }}>
                                <Typography variant="caption" fontWeight={600}>
                                    Title
                                </Typography>
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="caption" fontWeight={600}>
                                    Trade
                                </Typography>
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="caption" fontWeight={600}>
                                    Facility
                                </Typography>
                            </Box>
                            <Box sx={{ flex: 1, textAlign: "right" }}>
                                <Typography variant="caption" fontWeight={600}>
                                    Hours
                                </Typography>
                            </Box>
                            <Box sx={{ flex: 1, textAlign: "right" }}>
                                <Typography variant="caption" fontWeight={600}>
                                    Cost
                                </Typography>
                            </Box>
                        </Stack>
                        <Repeat list={pages.items} item={<VariantBreakdownTaskRow currency={currency} />} keyFn="id" />
                        <If value={loading}>
                            <Stack alignItems="center" sx={{ py: 1.5 }}>
                                <CircularProgress size={20} />
                            </Stack>
                        </If>
                        <If value={pages.hasNextPage && !loading}>
                            <Box sx={{ p: 1, textAlign: "center" }}>
                                <Button size="small" onClick={handleShowMore} data-cy="variant-breakdown-show-more">
                                    Show more ({filteredCount - pages.items.length} remaining)
                                </Button>
                            </Box>
                        </If>
                    </Box>
                </If>
            </Box>
        </If>
    )

    // Condition names are not unique (conditions are keyed by rule id), so the menu
    // dedupes the names and the filter sends EVERY key sharing the selected name,
    // matching the old client-side name filter.
    function getConditionalNames() {
        const conditions = pages.conditionOptions?.conditions ?? []
        return [...new Set(conditions.map(toName))].sort()

        function toName(condition) {
            return condition.name
        }
    }

    function filterArgs(filter) {
        if (
            filter === RESOLUTION_FILTER_DEFAULT_ONLY ||
            filter === RESOLUTION_FILTER_CONDITIONAL_ONLY ||
            filter === RESOLUTION_FILTER_ASSIGNMENT_ONLY
        ) {
            return { resolution: filter }
        }
        if (filter !== RESOLUTION_FILTER_ANY) {
            const conditionKeys = (pages.conditionOptions?.conditions ?? []).filter(byName).map(toKey)
            if (conditionKeys.length) return { conditionKeys }
        }
        return {}

        function byName(condition) {
            return condition.name === filter
        }

        function toKey(condition) {
            return condition.conditionKey
        }
    }

    // Only the most recent request may touch state. Switching the filter while a page
    // is in flight leaves two promises running, and if the older one settles last it
    // would show the previous filter's rows and totals under the newly selected
    // filter, or clear the spinner while the current request is still running.
    async function loadPage(filter, after) {
        const request = ++latestRequest.current
        setLoading(true)
        setError(null)
        try {
            const page = await getScenarioCostTasks({
                scenarioId,
                variantId: variant.id,
                setKey: costResult.detailsRef.setKey,
                first: TASK_PAGE_SIZE,
                after: after || undefined,
                ...filterArgs(filter),
            })
            if (request !== latestRequest.current) return
            setPages(function mergePage(previous) {
                return {
                    items: after ? [...previous.items, ...page.items] : page.items,
                    endCursor: page.endCursor,
                    hasNextPage: page.hasNextPage,
                    totalCount: page.totalCount,
                    conditionOptions: page.conditionOptions ?? previous.conditionOptions,
                }
            })
        } catch (e) {
            if (request !== latestRequest.current) return
            setError(e.message || "Could not load task detail")
        } finally {
            if (request === latestRequest.current) setLoading(false)
        }
    }

    function handleToggleTasks() {
        const opening = !showTasks
        setShowTasks(opening)
        if (opening && !pages.items.length && !loading) {
            loadPage(resolutionFilter)
        }
    }

    function handleShowMore() {
        loadPage(resolutionFilter, pages.endCursor)
    }

    function handleResolutionFilterChange(e) {
        const nextFilter = e.target.value
        setResolutionFilter(nextFilter)
        setPages((previous) => ({ ...EMPTY_PAGES, conditionOptions: previous.conditionOptions }))
        if (showTasks) loadPage(nextFilter)
    }
}
